import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { EmptyStateComponentProps } from "@appfire/poker-core/lib/types/EmptyStateIComponent";
import Button from "@atlaskit/button";
import { Box, Heading, Text } from "@fuegokit/react";

import { IssueSelected } from "../../../../analytics/ampli";
import { useRecordEvent } from "../../../../analytics/hooks/events/useRecordEvent";
import { JiraIssue } from "../../../../types";
import { useGameData } from "../../GameProvider";

interface EmptyStateModeratorProps {
  items: JiraIssue[];
  onSelectFirstAvailableActiveItem?: (newActiveItem: JiraIssue) => void;
}

function EmptyWelcomeStateModerator({ items, onSelectFirstAvailableActiveItem }: EmptyStateModeratorProps) {
  const recordIssueSelectedEvent = useRecordEvent(IssueSelected);
  const { gameId } = useGameData();

  const selectFirstAvailableActiveItem = useCallback(() => {
    if (items.length === 0) return;

    const itemToSelect = items[0];
    onSelectFirstAvailableActiveItem?.(itemToSelect);
    recordIssueSelectedEvent({ game_id: gameId, issue_id: itemToSelect.id });
  }, [gameId, items, onSelectFirstAvailableActiveItem, recordIssueSelectedEvent]);

  return (
    <Box mb="40px" display="flex" flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
      <img src="/images/game_empty_state_moderator.svg" alt="Poker cards" width={132} height={132} />
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 2 }}>
        <Heading variant="h600">Welcome to Planning Poker game</Heading>
        <Text textAlign="center">Click backlog issue to estimate or start game</Text>
      </Box>
      <Button appearance="primary" onClick={selectFirstAvailableActiveItem} testId="start-game-button">
        Start game
      </Button>
    </Box>
  );
}

function EmptyWelcomeStateParticipant() {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
      <img src="/images/game_empty_state_participant.svg" alt="Clock" width={100} height={100} />
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 2 }}>
        <Heading variant="h600">Welcome to Planning Poker game</Heading>
        <Text textAlign="center">Wait for the admin to start the game</Text>
      </Box>
    </Box>
  );
}

export function EmptyWelcomeState({ items, isModerator, onActiveItemChange }: EmptyStateComponentProps<JiraIssue>) {
  return (
    <Box display="flex" justifyContent="center">
      {isModerator ? (
        <EmptyWelcomeStateModerator items={items} onSelectFirstAvailableActiveItem={onActiveItemChange} />
      ) : (
        <EmptyWelcomeStateParticipant />
      )}
    </Box>
  );
}

export function EmptyBacklogState({ isModerator }: EmptyStateComponentProps<JiraIssue>) {
  const navigate = useNavigate();
  const { gameId } = useGameData();
  return (
    <Box mb="40px" display="flex" flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
      <img src="/icon/no-issues-glass.svg" alt="No issues glass" width={132} height={132} />
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 2 }}>
        <Heading variant="h600">There are no issues to estimate</Heading>
        <Text textAlign="center">{isModerator ? "Add more issues" : "Wait for admin to add the issues"}</Text>
      </Box>
      {isModerator && (
        <Button appearance="primary" onClick={() => navigate(`/game/${gameId}/backlog`)}>
          Edit backlog
        </Button>
      )}
    </Box>
  );
}

export function EmptyIssueState({ isModerator }: EmptyStateComponentProps<JiraIssue>) {
  return (
    <Box display="flex" justifyContent="center">
      <Box mb="40px" display="flex" flexDirection="column" alignItems="center" sx={{ gap: 3 }}>
        <img src="/icon/no-issue-selected.svg" alt="Cards with question mark" width={132} height={132} />
        <Box display="flex" flexDirection="column" alignItems="center" sx={{ gap: 2 }}>
          {isModerator ? (
            <>
              <Heading variant="h600">No issue selected</Heading>
              <Text textAlign="center">Click backlog issue to estimate</Text>
            </>
          ) : (
            <>
              <Heading variant="h600">No issue selected</Heading>
              <Text textAlign="center">Wait for the admin to select an issue</Text>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
